import { Plus, Radio, Webhook } from 'lucide-react';
import React from 'react';
import { StatusChip } from '../components/Avatar';
import { EmptyState } from '../components/EmptyState';
import { SectionCard } from '../components/SectionCard';
import { useWorkspace } from '../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../hooks/useActionDialog';
import { formatAbsoluteDate, formatRelativeDate } from '../utils/formatters';

type AlertSourcesPageProps = {
  openDialog: UseActionDialogReturn['openDialog'];
};

function AlertSourcesPage({ openDialog }: AlertSourcesPageProps) {
  const { workspace, activeOrganization, activeProject } = useWorkspace();
  const { ingestionSources } = workspace;

  const sortedSources = [...ingestionSources].sort((a, b) =>
    (b.created || '').localeCompare(a.created || '')
  );

  function handleAddSource(sourceKind: 'generic' | 'grafana') {
    if (!activeOrganization) return;
    openDialog({
      kind: 'ingestion-source',
      organizationId: activeOrganization.id,
      projectId: activeProject?.id || '',
      name: '',
      sourceKind,
      targetEvidenceType: sourceKind === 'grafana' ? 'alert' : 'alert'
    });
  }

  return (
    <SectionCard
      label="Ingestion"
      title="Alert sources"
      description="Receive alerts from Grafana and other systems through signed webhooks."
      action={
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" className="secondaryButton" onClick={() => handleAddSource('generic')}>
            <Plus size={14} />
            <span>Add source</span>
          </button>
          <button type="button" className="primaryButton" onClick={() => handleAddSource('grafana')}>
            <Plus size={14} />
            <span>Add Grafana source</span>
          </button>
        </div>
      }
    >
      {sortedSources.length > 0 ? (
        <div className="releaseTableWrap" role="region" aria-label="Alert sources table" tabIndex={0}>
          <table className="releaseTable">
            <thead>
              <tr>
                <th>Name</th>
                <th>Kind</th>
                <th>Evidence type</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {sortedSources.map((source) => (
                <tr key={source.ingestionSourceId}>
                  <td>
                    <div className="releaseIdentity">
                      <strong>{source.name}</strong>
                      <span>{source.webhookPath}</span>
                    </div>
                  </td>
                  <td>
                    <StatusChip tone={source.sourceKind === 'grafana' ? 'success' : 'neutral'}>
                      {source.sourceKind === 'grafana' ? 'Grafana' : 'Generic'}
                    </StatusChip>
                  </td>
                  <td>{source.targetEvidenceType}</td>
                  <td>
                    <StatusChip tone={source.status === 'active' ? 'success' : 'neutral'}>
                      {source.status === 'active' ? 'Active' : 'Paused'}
                    </StatusChip>
                  </td>
                  <td>
                    <div className="releaseIdentity">
                      <strong>{formatRelativeDate(source.created)}</strong>
                      <span>{formatAbsoluteDate(source.created)}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState
          icon={<Webhook size={20} />}
          title="No alert sources yet"
          description="Add a Grafana source to receive unified-alerting webhooks, or a generic source for any other tool that can send a webhook."
          action={
            <button type="button" className="primaryButton" onClick={() => handleAddSource('grafana')}>
              <Radio size={14} />
              <span>Add Grafana source</span>
            </button>
          }
        />
      )}
    </SectionCard>
  );
}

export { AlertSourcesPage };
